import React, { useEffect, useState, useCallback } from "react";
import ClinicalValidationPlanForm from "../components/ClinicalValidationPlanForm";

// Super-admin Resources library. A catalog of editable document TEMPLATES that can be
// attached to a client proposal (Clients → Proposals → Documents) and filled out. The
// first document is a comprehensive Clinical Validation Plan. Calls /api/super-admin/resources.

const box = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 16 };
const btn = { padding: "8px 14px", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 };
const primary = { ...btn, background: "#2563eb", color: "#fff" };
const subtle = { ...btn, background: "#e2e8f0", color: "#0f172a" };
const th = { textAlign: "left", padding: "8px 10px", borderBottom: "2px solid #273d3b", background: "#273d3b", color: "#fff" };
const td = { padding: "8px 10px", borderBottom: "1px solid #e2e8f0" };

function authHeaders(json = true) {
    const h = { Authorization: `Bearer ${localStorage.getItem("idToken")}` };
    if (json) h["Content-Type"] = "application/json";
    return h;
}
const api = (url, opts = {}) => fetch(url, { headers: authHeaders(), credentials: "include", ...opts });

export default function ResourcesPage() {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState("");
    const [editing, setEditing] = useState(null); // { doc, template }
    const [flash, setFlash] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api("/api/super-admin/resources");
            const data = await res.json();
            setDocs(data.documents || []);
        } catch (e) { setMessage("Failed to load resources."); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const openDoc = async (id) => {
        setFlash(null);
        const res = await api(`/api/super-admin/resources/${id}`);
        const data = await res.json();
        setEditing({ doc: data.document, template: data.document.template || {} });
    };

    const saveTemplate = async () => {
        setBusy(true); setFlash(null);
        try {
            const res = await api(`/api/super-admin/resources/${editing.doc.id}`, {
                method: "PUT", body: JSON.stringify({ template: editing.template }),
            });
            if (!res.ok) throw new Error((await res.json()).error || "Failed");
            setFlash({ kind: "success", text: "Template saved." });
        } catch (e) { setFlash({ kind: "error", text: e.message }); } finally { setBusy(false); }
    };

    return (
        <div style={{ padding: 20 }}>
            <h2 style={{ marginTop: 0 }}>Resources</h2>
            <p style={{ color: "#64748b", marginTop: -6 }}>
                A library of editable document templates. Attach these to a client's proposal (Clients → Proposals → Documents) and fill them out per engagement.
            </p>
            {message && <div style={{ ...box, borderColor: "#fca5a5", background: "#fef2f2", color: "#b91c1c" }}>{message}</div>}

            {loading ? <p>Loading…</p> : (
                <div style={box}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead><tr><th style={th}>Document</th><th style={th}>Type</th><th style={th}>Description</th><th style={th}>Updated</th><th style={th}></th></tr></thead>
                        <tbody>
                            {docs.map((d) => (
                                <tr key={d.id}>
                                    <td style={{ ...td, fontWeight: 600 }}>{d.title}</td>
                                    <td style={td}>{d.doc_type.replace(/_/g, " ")}</td>
                                    <td style={{ ...td, color: "#64748b", fontSize: 13 }}>{d.description}</td>
                                    <td style={td}>{d.updated_at ? new Date(d.updated_at).toLocaleDateString() : "—"}</td>
                                    <td style={td}><button style={subtle} onClick={() => openDoc(d.id)}>Edit template</button></td>
                                </tr>
                            ))}
                            {!docs.length && <tr><td style={td} colSpan={5}>No documents in the library.</td></tr>}
                        </tbody>
                    </table>
                </div>
            )}

            {editing && (
                <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
                    <div style={{ width: "90vw", height: "90vh", background: "#f8fafc", borderRadius: 12, padding: 24, overflow: "auto", position: "relative" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, position: "sticky", top: 0, background: "#f8fafc", paddingBottom: 8, zIndex: 1 }}>
                            <div>
                                <h3 style={{ margin: 0 }}>{editing.doc.title}</h3>
                                <div style={{ fontSize: 12, color: "#64748b" }}>Editing the library template defaults (applied to new attachments).</div>
                            </div>
                            <div style={{ display: "flex", gap: 8 }}>
                                <button style={primary} disabled={busy} onClick={saveTemplate}>Save template</button>
                                <button style={subtle} onClick={() => { setEditing(null); setFlash(null); }}>Close</button>
                            </div>
                        </div>
                        {flash && <div style={{ margin: "6px 0 12px", padding: "10px 14px", borderRadius: 8, background: flash.kind === "success" ? "#f0fdf4" : "#fef2f2", color: flash.kind === "success" ? "#166534" : "#b91c1c", border: `1px solid ${flash.kind === "success" ? "#bbf7d0" : "#fca5a5"}` }}>{flash.text}</div>}
                        {editing.doc.doc_type === "clinical_validation_plan" ? (
                            <ClinicalValidationPlanForm value={editing.template} onChange={(t) => setEditing({ ...editing, template: t })} />
                        ) : (
                            <div style={box}>This document type has no structured editor yet.</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
